import { useMemo } from "react";

import {
  ENTHUSIAST_SEATS_LIMIT,
  ENTHUSIAST_SLOTS_LIMIT,
} from "components/subscription/useTiers";
import { useAuthenticatedUser } from "contexts/useAuthenticatedUserContext";
import { hasExternalPartner as checkHasExternalPartner } from "typings/Organization";
import { TierLevel } from "typings/Tier";

type OverQuota = Partial<{
  canDowngrade: boolean;
  canEdit: boolean;
  canEncrypt: boolean;
  crewOverQuota: boolean;
  hasCancelledSubscription: boolean;
  hasExternalPartner: boolean;
  hasVehicles: boolean;
  inTrialPeriod: boolean;
  isExpired: boolean;
  isSubscribed: boolean;
  vehicleOverQuota: boolean;
}>;

export const useCheckQuotas = (): OverQuota => {
  const {
    actingOnBehalfOfId,
    organizationsIds,
    quota,
    subscriptions,
    tier,
    tierExpirationDate,
    tierLevel,
  } = useAuthenticatedUser();

  return useMemo(() => {
    const {
      crewCount = ENTHUSIAST_SEATS_LIMIT,
      /** err on the side of caution, defaults to limit */
      vehicleCount = ENTHUSIAST_SLOTS_LIMIT,
    } = quota ?? {};

    const isExpired = Number(tierExpirationDate) < Date.now();

    const vehicleQuotaAsEnthusiast =
      TierLevel.isEnthusiast(tierLevel) &&
      vehicleCount >= ENTHUSIAST_SLOTS_LIMIT;

    const hasExternalPartner = checkHasExternalPartner(organizationsIds);

    return {
      canDowngrade:
        vehicleCount <= ENTHUSIAST_SLOTS_LIMIT &&
        crewCount <= ENTHUSIAST_SEATS_LIMIT,

      canEdit: TierLevel.hasEnthusiast(tierLevel) || hasExternalPartner,

      canEncrypt: TierLevel.isPro(tierLevel) && !actingOnBehalfOfId,

      crewOverQuota: !TierLevel.isPro(tierLevel),

      hasCancelledSubscription: !subscriptions?.[0]?.isRenewed,

      hasExternalPartner,

      hasVehicles: vehicleCount > 0,

      inTrialPeriod: !isExpired && !subscriptions?.length,

      isExpired,

      isSubscribed: !!tier,

      vehicleOverQuota:
        vehicleQuotaAsEnthusiast || !TierLevel.hasPro(tierLevel),
    };
  }, [
    actingOnBehalfOfId,
    organizationsIds,
    quota,
    tierExpirationDate,
    tierLevel,
  ]);
};
